import {
  Attachment,
  BoxStatusType,
  Connection,
  CustomValue,
  Lead, LeadStatus, Operation, OperationChild, OperationInfo,
  PermissionCode, ProjectsPool, Task, TaskName, User,
} from '@/types';
import store from '@/store';
import moment from 'moment';

moment.locale('it');

// currentUser

function currentUserIsSuper(): boolean {
  const { currentUser }: { currentUser: User | null } = store.getters;
  return currentUser?.isSuperUser === true;
}

function currentUserIsRequester(): boolean {
  const { currentUser, currentLead }: {
    currentUser: User | null; currentLead: Lead | null;
  } = store.getters;

  return currentUser?.id === currentLead?.requesterId;
}

// Verifica se l'utente attuale possiede un determinato permesso
function currentUserHasPermission(codename: PermissionCode): boolean {
  const { currentUser, currentUserPermissions } = store.getters;

  if (!currentUser) return false;
  if (currentUser.isSuperUser) return true;

  return currentUserPermissions.includes(codename);
}

// Task

function isTaskCompleted(operationChild: OperationChild | null, name: string): boolean {
  return operationChild?.flatTasks.includes(`${name}=c`) ?? false;
}

function isTaskFailed(operationChild: OperationChild | null, name: string): boolean {
  return operationChild?.flatTasks.includes(`${name}=e`) ?? false;
}

function getTaskFromLead(name: TaskName, lead: Lead): Task | null {
  return lead?.Tasks.find((t) => t.name === name) ?? null;
}

function getPrevTask(name: string): Task | null {
  const { currentLead: selectedLead }: { currentLead: Lead | null } = store.getters;

  let prevTask: Task | null = null;

  if (selectedLead?.Tasks) {
    // eslint-disable-next-line no-restricted-syntax
    for (const task of selectedLead.Tasks.filter((t) => t.status !== 'disabled')) {
      if (task.name === name) {
        break;
      }

      prevTask = task;
    }
  }

  return prevTask;
}

/**
 * Mappatura: tipo task → permesso soft (reset solo se nessun successivo è completato o in errore).
 */
const RESET_PERMISSION_BY_TASK_NAME: Partial<Record<TaskName, PermissionCode>> = {
  C: 'taskc.status.reset',
  P: 'taskp.status.reset',
  O: 'tasko.status.reset',
  Cm: 'taskcm.status.reset',
  S: 'tasks.status.reset',
  A: 'taska.status.reset',
  V: 'taskv.status.reset',
  M: 'taskm.status.reset',
  I: 'taski.status.reset',
  R: 'taskr.status.reset',
};

/**
 * Mappatura: tipo task → permesso force (reset libero, nessun check sui successivi).
 */
const FORCE_RESET_PERMISSION_BY_TASK_NAME: Partial<Record<TaskName, PermissionCode>> = {
  C: 'taskc.status.reset.force',
  P: 'taskp.status.reset.force',
  O: 'tasko.status.reset.force',
  Cm: 'taskcm.status.reset.force',
  S: 'tasks.status.reset.force',
  A: 'taska.status.reset.force',
  V: 'taskv.status.reset.force',
  M: 'taskm.status.reset.force',
  I: 'taski.status.reset.force',
  R: 'taskr.status.reset.force',
};

/**
 * Determina se il task specificato può essere reimpostato dall'utente
 * corrente. Logica unificata condivisa da TaskActions, TaskStatusAlert
 * e TaskDetailsTitle.
 *
 * - SuperUser: sempre consentito.
 * - task{x}.status.reset.force: reset libero (nessun check sui successivi).
 * - task{x}.status.reset: reset consentito solo se nessun task successivo
 *   è già completato o in errore (i task 'disabled' non bloccano il reset).
 */
function canResetTask(task: Task | null, lead: Lead | null): boolean {
  if (!task || !lead) return false;

  // Se il task è disabilitato o gestito da una lead figlia
  // o se la lead non è in lavorazione...
  if (task.status === 'disabled'
    || task.relationship === 'father'
    || lead.status !== 'open') {
    return false;
  }

  // Il SuperUser può sempre reimpostare i task reimpostabili
  if (currentUserIsSuper()) return true;

  const forcePerm = FORCE_RESET_PERMISSION_BY_TASK_NAME[task.name];
  if (forcePerm && currentUserHasPermission(forcePerm)) return true;

  const softPerm = RESET_PERMISSION_BY_TASK_NAME[task.name];
  if (!softPerm || !currentUserHasPermission(softPerm)) return false;

  // Permesso soft: consentito solo se nessun task successivo è già completato
  // o in errore (i task 'disabled' non bloccano il reset)
  let found = false;
  let hasCompletedSuccessor = false;
  lead.Tasks.forEach((t) => {
    if (t.id === task.id) {
      found = true;
      return;
    }
    if (found && (t.status === 'completed' || t.status === 'error')) {
      hasCompletedSuccessor = true;
    }
  });

  return !hasCompletedSuccessor;
}

function canEditTaskStatus(): boolean {
  const { selectedTask }: { selectedTask: Task | null } = store.getters;
  const { currentLead: selectedLead }: { currentLead: Lead | null } = store.getters;

  if (!selectedTask || !selectedLead) return false;

  // Il SuperUser può sempre modificare i task modificabili
  if (currentUserIsSuper()) return true;

  // Verifica prima i permessi
  let permission: string | null = null;
  switch (selectedTask.name) {
    case 'C':
      permission = `leads_${selectedLead.type}.task_appuntamento_visita`;
      break;
    case 'P':
      permission = `leads_${selectedLead.type}.task_preventivo`;
      break;
    case 'O':
      permission = `leads_${selectedLead.type}.task_ordine`;
      break;
    case 'Cm':
      permission = 'taskcm.status.edit';
      break;

    case 'S':
      permission = `leads_${selectedLead.type}.task_appuntamento`;
      break;
    case 'A':
      permission = `leads_${selectedLead.type}.task_conferma`;
      break;
    case 'V':
      permission = 'taskv.status.edit';
      break;
    case 'M':
      permission = 'taskm.status.edit';
      break;
    case 'I':
      permission = 'taski.status.edit';
      break;
    case 'R':
      permission = 'taskr.status.edit';
      break;

    default:
      break;
  }

  if (!permission || !currentUserHasPermission(permission)) return false;

  // Verifica lo stato del passo precedente e del successivo
  const prevTask: Task | null = getPrevTask(selectedTask.name);
  // const nextTask: Task | null = UTILS.getNextTask(this.lead, this.task.name);

  // Se l'utente non ha il permesso per completare i task successivi a un task incompleto...
  if (!currentUserHasPermission('task.status.can_complete')) {
    // Verifica che il task precedente (se esiste) sia completo
    if (prevTask && 'status' in prevTask && prevTask.status !== 'completed') {
      return false;
    }
  }

  // // La prossima fase (se esiste e non è padre) deve essere incompleta
  // if (nextTask && (nextTask as any)?.status !== null
  //   && (nextTask as any).relationship !== 'father') {
  //   return false;
  // }

  // // Non permette di annullare o correggere se:
  // // la prossima fase è stata assegnata ad una lead figlia
  // // e il task corrente è stato completato/errato
  // if (nextTask && nextTask.relationship === 'father' && nextTask.childLeadId !== null
  //   && this.task?.status !== null) {
  //   return false;
  // }

  return true;
}

// lead

function getLeadCustomValue(lead: Lead | null, name: string): CustomValue | null {
  if (!lead?.CustomValues
    || !Array.isArray(lead.CustomValues)
    || lead.CustomValues.length === 0) {
    return null;
  }

  const cv = lead.CustomValues.find((v) => v.name === name);

  return cv ?? null;
}

function getLeadCustomValueBoolean(lead: Lead | null, name: string): boolean | null {
  const cv = getLeadCustomValue(lead, name);

  return cv?.booleanValue !== undefined ? Boolean(cv.booleanValue) : null;
}

function getLeadCustomValueText(lead: Lead | null, name: string): string | null {
  const cv = getLeadCustomValue(lead, name);

  return cv?.value !== undefined ? cv.value : null;
}

function getLeadCustomValueDate(lead: Lead | null, name: string): Date | null {
  const cv = getLeadCustomValue(lead, name);

  return cv?.dateValue !== undefined ? cv.dateValue : null;
}

// selectedLead

function selectedLeadStatusIs(status: LeadStatus): boolean {
  const { currentLead }: { currentLead: Lead | null } = store.getters;

  if (!currentLead?.status) return false;

  return currentLead.status === status;
}

function getSelectedLeadCustomValue(name: string): CustomValue | null {
  const { currentLead }: { currentLead: Lead | null } = store.getters;

  return getLeadCustomValue(currentLead, name);
}

function getSelectedLeadCustomValueBoolean(name: string): boolean | null {
  const { currentLead }: { currentLead: Lead | null } = store.getters;

  return getLeadCustomValueBoolean(currentLead, name);
}

function getSelectedLeadCustomValueText(name: string): string | null {
  const { currentLead }: { currentLead: Lead | null } = store.getters;

  return getLeadCustomValueText(currentLead, name);
}

function getSelectedLeadCustomValueDate(name: string): Date | null {
  const { currentLead }: { currentLead: Lead | null } = store.getters;

  return getLeadCustomValueDate(currentLead, name);
}

function supply1() { return getSelectedLeadCustomValueBoolean('supply1') ?? false; }
function supply2() { return getSelectedLeadCustomValueBoolean('supply2') ?? false; }
function supply3() { return getSelectedLeadCustomValueBoolean('supply3') ?? false; }
function supply4() { return getSelectedLeadCustomValueBoolean('supply4') ?? false; }
function supply5() { return getSelectedLeadCustomValueBoolean('supply5') ?? false; }

function supply1text() { return getSelectedLeadCustomValueText('supply1text'); }
function supply2text() { return getSelectedLeadCustomValueText('supply2text'); }
function supply3text() { return getSelectedLeadCustomValueText('supply3text'); }
function supply4text() { return getSelectedLeadCustomValueText('supply4text'); }
function supply5text() { return getSelectedLeadCustomValueText('supply5text'); }

function seletedLeadHasSupply(): boolean {
  if (supply5() && !!supply5text()) return true;
  if (supply4() && !!supply4text()) return true;
  if (supply3() && !!supply3text()) return true;
  if (supply2() && !!supply2text()) return true;
  if (supply1() && !!supply1text()) return true;
  return false;
}

// selectedTask

function selectedTaskFatherLead(): Lead | null {
  const { selectedTask, currentPool }: {
    selectedTask: Task | null;
    currentPool: ProjectsPool;
  } = store.getters;

  if (!selectedTask || 'fatherLeadId' in selectedTask === false) return null;

  const fatherLeadId = selectedTask.relationship === 'child'
    ? selectedTask.fatherLeadId
    : selectedTask.LeadId;

  return fatherLeadId && fatherLeadId in currentPool
    ? currentPool[fatherLeadId]
    : null;
}

// Task

function taskFatherLead(task: Task | null): Lead | null {
  const { currentPool }: {
    currentPool: ProjectsPool;
  } = store.getters;

  if (!task || 'fatherLeadId' in task === false) return null;

  const fatherLeadId = task.relationship === 'child'
    ? task.fatherLeadId
    : task.LeadId;

  return fatherLeadId && fatherLeadId in currentPool
    ? currentPool[fatherLeadId]
    : null;
}

function chipColor(value: BoxStatusType | null): string {
  switch (value) {
    case null:
    case 'todo':
      return 'grey lighten-2';

    case 'done':
      return 'statusDone';

    case 'waiting':
      return 'orange';

    case 'na':
      return 'grey';

    default:
      break;
  }

  return '';
}

function chipOutlined(value: BoxStatusType | null): boolean {
  switch (value) {
    case 'na':
      return true;

    default:
      break;
  }

  return false;
}

function tooltipColor(value: BoxStatusType | null): string {
  switch (value) {
    case null:
    case 'todo':
      return 'grey';

    case 'done':
      return 'success';

    case 'waiting':
      return 'orange';

    case 'na':
      return 'grey';

    default:
      break;
  }

  return '';
}

function tooltipText(prefix: string, value: BoxStatusType | null): string {
  switch (value) {
    case null:
    case 'todo':
      return `${prefix} (da fare)`;

    case 'na':
      return `${prefix} (non applicabile)`;

    case 'waiting':
      return `${prefix} (in attesa di terzi)`;

    default:
      break;
  }
  return prefix;
}

//

function operationInfoFromConnection(connection: Connection): OperationInfo {
  return connection as OperationInfo;
}

function operationInfoFromOperation(operation: Operation): OperationInfo {
  return operation as OperationInfo;
}

function getLeadAttachments(lead: Lead | null): Attachment[] {
  return lead?.Attachments ?? [];
}

const isBlank = (str) => (!str || /^\s*$/.test(str));

// Totale contrattuale. Nel DB e' un importo canonico ("12345.5", vedi
// backend/app/utils/isCanonicalAmount.js); all'utente si mostra e si fa
// scrivere in italiano ("12.345,50").

const CANONICAL_AMOUNT = /^\d+(\.\d{1,2})?$/;
const ITALIAN_AMOUNT = /^(\d{1,3}(\.\d{3})*|\d+)(,\d{1,2})?$/;

function isCanonicalAmount(value: string | null | undefined): boolean {
  return !!value && CANONICAL_AMOUNT.test(value);
}

// "12345.5" -> "12.345,50". Un valore non canonico torna com'e': non si nasconde
function formatEuroNumber(value: string | null | undefined): string {
  if (!value) return '';
  if (!isCanonicalAmount(value)) return value;
  const num = parseFloat(value).toFixed(2);
  const [integer, decimals] = num.split('.');
  const withThousandsSeparator = integer.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  return `${withThousandsSeparator},${decimals}`;
}

// "12345.5" -> "€ 12.345,50"
function formatEuro(value: string | null | undefined): string {
  if (!value) return '';
  if (!isCanonicalAmount(value)) return value;
  return `€ ${formatEuroNumber(value)}`;
}

// "12.345,50" | "12345" | "12345,5" | "€ 1.234" -> canonico; "" -> "";
// null se il testo non e' un importo in formato italiano
function parseEuroInput(text: string | null | undefined): string | null {
  if (text === null || text === undefined) return '';
  const cleaned = text.replace(/€/g, '').replace(/\s/g, '');
  if (cleaned === '') return '';
  if (!ITALIAN_AMOUNT.test(cleaned)) return null;
  const [integer, decimals = ''] = cleaned.replace(/\./g, '').split(',');
  return decimals ? `${integer}.${decimals.padEnd(2, '0')}` : integer;
}

// Assignment label
function getAssignmentLabel(
  config: { label: string; childLabels?: Record<string, string> },
  childType?: string | null,
): string {
  if (childType && config.childLabels?.[childType]) {
    return config.childLabels[childType];
  }
  return config.label;
}

// Utility functions

// Escape HTML per prevenire XSS
function escapeHtml(str: string | null | undefined): string {
  if (!str) return '';
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

// Prima lettera maiuscola nel testo, non via CSS: il copia-incolla prende il testo
// com'e' salvato (es. il toponimo 'via' della tendina), non come lo mostra il CSS
function capitalizeFirst(str: string): string {
  return str.charAt(0).toUpperCase() + str.slice(1);
}

// Formatta una data usando moment (formato 'L' - DD/MM/YYYY)
function formatDate(date: Date | string | null | undefined): string | null {
  if (!date) return null;
  return moment(date).format('L');
}

// Payments

interface PaymentData {
  paymentDate: Date | string | null | undefined;
  invoiceRef: string | null | undefined;
  invoiceDate: Date | string | null | undefined;
  label: string;
}

interface PaymentsData {
  first: PaymentData;
  second: PaymentData;
  partialInstallation: PaymentData;
  final: PaymentData;
}

// Genera HTML formattato per i dettagli dei pagamenti
function formatPaymentsDetails(
  paymentsData: PaymentsData,
  notes?: string | null,
): string {
  const payments = [
    paymentsData.first,
    paymentsData.second,
    paymentsData.partialInstallation,
    paymentsData.final,
  ];

  // Fatture pagate (con data pagamento)
  const paidPayments = payments.filter((p) => p.paymentDate);

  // Fatture da pagare (riferimento o data fattura presenti, ma senza data pagamento)
  const unpaidPayments = payments.filter((p) => !p.paymentDate
    && (p.invoiceRef || p.invoiceDate));

  let result = '';

  // Genera output per fatture pagate
  if (paidPayments.length > 0) {
    result += '<b>Pagate</b><ul style="margin: 0; padding-left: 1.2em;">';
    paidPayments.forEach((payment) => {
      const { paymentDate, invoiceRef, invoiceDate } = payment;

      let line = `<b>${payment.label}:</b>`;

      if (invoiceRef || invoiceDate) {
        line += ' fattura';
        if (invoiceRef) {
          line += ` <i>${escapeHtml(invoiceRef)}</i>`;
        }
        if (invoiceDate) {
          const formattedDate = formatDate(invoiceDate);
          if (formattedDate) {
            line += ` del ${formattedDate}`;
          }
        }
        const formattedPaymentDate = formatDate(paymentDate);
        if (formattedPaymentDate) {
          line += ` (pagata il ${formattedPaymentDate})`;
        }
      } else {
        const formattedPaymentDate = formatDate(paymentDate);
        if (formattedPaymentDate) {
          line += ` pagata il ${formattedPaymentDate}`;
        }
      }

      result += `<li>${line}</li>`;
    });
    result += '</ul>';
  }

  // Genera output per fatture da pagare
  if (unpaidPayments.length > 0) {
    if (paidPayments.length > 0) {
      result += '<br>';
    }
    result += '<b>Da pagare</b><ul style="margin: 0; padding-left: 1.2em;">';
    unpaidPayments.forEach((payment) => {
      const { invoiceRef, invoiceDate } = payment;

      let line = `<b>${payment.label}:</b> fattura`;
      if (invoiceRef) {
        line += ` <i>${escapeHtml(invoiceRef)}</i>`;
      }
      if (invoiceDate) {
        const formattedDate = formatDate(invoiceDate);
        if (formattedDate) {
          line += ` del ${formattedDate}`;
        }
      }

      result += `<li>${line}</li>`;
    });
    result += '</ul>';
  }

  // Aggiungi note se presenti
  if (notes) {
    if (paidPayments.length > 0 || unpaidPayments.length > 0) {
      result += '<br>';
    }
    // Le note arrivano sempre come testo semplice: l'escape si fa solo qui
    result += escapeHtml(notes).replace(/\n/g, '<br>');
  }

  return result;
}

export {
  isTaskCompleted,
  isTaskFailed,
  getTaskFromLead,
  canEditTaskStatus,
  canResetTask,

  // currentUser
  currentUserIsSuper,
  currentUserIsRequester,
  currentUserHasPermission,

  // selectedLead
  selectedLeadStatusIs,
  getSelectedLeadCustomValue,
  getSelectedLeadCustomValueBoolean,
  getSelectedLeadCustomValueText,
  getSelectedLeadCustomValueDate,
  seletedLeadHasSupply,

  // selectedTask
  selectedTaskFatherLead,

  // Lead
  getLeadCustomValue,
  getLeadCustomValueBoolean,
  getLeadCustomValueText,
  getLeadCustomValueDate,

  // Task
  taskFatherLead,

  //
  chipColor,
  chipOutlined,
  tooltipColor,
  tooltipText,

  //
  operationInfoFromConnection,
  operationInfoFromOperation,

  // Attachments
  getLeadAttachments,

  isBlank,

  // Totale contrattuale
  formatEuro,
  formatEuroNumber,
  parseEuroInput,

  // Assignment
  getAssignmentLabel,

  // Utility
  escapeHtml,
  capitalizeFirst,
  formatDate,

  // Payments
  formatPaymentsDetails,
};
